import {
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_UNAVAILABLE,
  FORGOT_PASSWORD_FAILURE,
  FORGOT_PASSWORD_REQUEST,
  CHANGE_PASSWORD_REQUEST,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_FAILURE,
  CLEAR_VALIDATION_ERRORS,
  LOGOUT_REQUEST,
  GET_PROFILE_DATA,
  SET_CURRENT_CODE,
  SET_CURRENT_PHONE,
  ADD_RESEND,
  SET_PHONE,
  RESET_RESEND_COUNT,
  TWO_FACTOR_ERROR,
  TWO_FACTOR_REJECTED,
  TWO_FACTOR_UNAVAILABLE,
  SET_CURRENT_BLOODBANK,
  SET_TO_ALL_BLOODBANKS,
  SET_PASSWORDS,
  UPDATE_TOKEN,
  SET_2FA,
  FINALIZE_LOGIN_REQUEST,
  FINALIZE_LOGIN_SUCCESS,
  FINALIZE_LOGIN_FAILURE,
  SET_CURRENT_EMAIL,
  SET_AUTH_SESSION_ID,
  SET_LOGIN_LOADING,
  SET_RESEND_LOADING,
  SET_FIREWALL_ERROR,
  SET_FEATURES,
} from './actionTypes';
import { createAction } from 'redux-actions';
import firebase from '../../services/firebase';
import redirect from '../../services/redirect';
import { addPhoneRequest ,apiForgotPassword,getCustomToken,getPasswordPolicy,setBloodBanks,signIn} from '../../services/fetch';
import { getRecaptchaToken } from '../../services/recaptcha';
import routes from '../../constants/routes';
import * as dateFns from "date-fns";
import {
  duplicatePasswordsError,
  getForgotPasswordValidationMessage,
  getLoginValidationMessage,
  getNewPasswordValidationMessage,
  passwordValidationError,
} from "../../services/validation";
import authCodes, { loginFailureReason, authFlowEvents } from '../../constants/authCodes';
import { isLoginUnavailable } from './loginFailureKind';
import { sendErrorLog, sendSigninAttemptLog, sendAuthEvent } from "../logging/actions";
import store from '../../services/store';
import {toast} from "react-toastify";
import { checkExternalUrlAccess } from '../../services/helpers';
import * as CryptoJS from 'crypto-js'
import { db } from '../../services/firebase';
import { normalizeFeatures } from '../../constants/features';
import { orderBanksByAccess } from './bankOrder';
// Bloodbank docs point at their department via `reference`; the HF id is
// the fourth path segment (services/bloodbankReference.js, shared with the
// CMT-57 donation-site move in «Giverdetaljer»).
import { getHfIdFromReference } from '../../services/bloodbankReference';

export const loginRequest = createAction(LOGIN_REQUEST);
export const loginSuccess = createAction(LOGIN_SUCCESS);
export const loginFailure = createAction(LOGIN_FAILURE);
// The service could not be reached / failed (network, 5xx, wrong API host) —
// NOT a credential decision. Rendered as loginUnavailable, never loginError.
export const loginUnavailable = createAction(LOGIN_UNAVAILABLE);
export const finalizeLoginRequest = createAction(FINALIZE_LOGIN_REQUEST);
export const finalizeLoginSuccess = createAction(FINALIZE_LOGIN_SUCCESS);
export const finalizeLoginFailure = createAction(FINALIZE_LOGIN_FAILURE);
export const updateToken = createAction(UPDATE_TOKEN);
export const forgotPasswordRequest = createAction(FORGOT_PASSWORD_REQUEST);
export const forgotPasswordFailure = createAction(FORGOT_PASSWORD_FAILURE);
export const changePasswordRequest = createAction(CHANGE_PASSWORD_REQUEST);
export const changePasswordSuccess = createAction(CHANGE_PASSWORD_SUCCESS);
export const changePasswordFailure = createAction(CHANGE_PASSWORD_FAILURE);
export const clearValidationErrors = createAction(CLEAR_VALIDATION_ERRORS);
export const userLogoutRequest = createAction(LOGOUT_REQUEST);
export const getProfileData = createAction(GET_PROFILE_DATA);
// Staff feature flags from the user's own admins doc (constants/features.js).
export const setFeatures = createAction(SET_FEATURES);
export const setCurrentCode = createAction(SET_CURRENT_CODE);
export const setCurrentPhone = createAction(SET_CURRENT_PHONE);
export const addResend = createAction(ADD_RESEND);
export const setPhone = createAction(SET_PHONE);
export const resetResendCount = createAction(RESET_RESEND_COUNT);
export const twoFactorError = createAction(TWO_FACTOR_ERROR);
// Post-finalize rejection (blocking function / backend), NOT a wrongly typed code.
export const twoFactorRejected = createAction(TWO_FACTOR_REJECTED);
export const twoFactorUnavailable = createAction(TWO_FACTOR_UNAVAILABLE);
export const setCurrentBloodbank = createAction(SET_CURRENT_BLOODBANK);
export const setToAllBloodbanks = createAction(SET_TO_ALL_BLOODBANKS);
export const setPasswords = createAction(SET_PASSWORDS);
export const setIsInTwoFactor = createAction(SET_2FA);
export const setCurrentEmail = createAction(SET_CURRENT_EMAIL);
export const setAuthSessionId = createAction(SET_AUTH_SESSION_ID);
export const setLoginLoading = createAction(SET_LOGIN_LOADING);
export const setResendLoading = createAction(SET_RESEND_LOADING);
export const setFirewallError = createAction(SET_FIREWALL_ERROR);

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const redirectToCurrentDayPage = () => {
  const dateFormat = "yyyy-MM-dd";
  const formattedDate = dateFns.format(new Date(), dateFormat);
  redirect(`${routes.DASHBOARD_DAY_ROUTE}/${formattedDate}`);
};

// The sections a blood-bank switch may return the user to (CMT-34). An
// allowlist, not a blocklist: the switcher itself, the sign-in and account
// routes, absolute URLs and protocol-relative paths all fall back to today's
// day view, so a crafted `from` can neither loop nor leave the app.
const RETURN_TO_SECTION_ROUTES = [
  routes.DASHBOARD_DAY_ROUTE,
  routes.DASHBOARD_WEEK_ROUTE,
  routes.DASHBOARD_MONTH_ROUTE,
  routes.CONFIGURATION_ROUTE,
  routes.DONORS_LIST_ROUTE,
  routes.PUSH_MESSAGES_ROUTE,
  routes.COUPLING_TABLE_ROUTE,
  routes.REPORTS_ROUTE,
];

export const isSafeReturnTo = (returnTo) => {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return false;
  }
  const pathname = returnTo.split(/[?#]/)[0];
  return RETURN_TO_SECTION_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
};

// `returnTo` is where the switcher was opened from (Profile menu ->
// ChangeBloodbank, via router state). Every section fetches for the store's
// current bank on mount, so landing back on it shows the new bank's data.
export const handleSetCurrentBloodbank = ({ returnTo, ...bloodbank }) => (dispatch) => {
  dispatch(setCurrentBloodbank(bloodbank));
  if (isSafeReturnTo(returnTo)) {
    redirect(returnTo);
    return;
  }
  redirectToCurrentDayPage();
};

export const clearAuthValidationErrors = () => (dispatch) => {
  dispatch(clearValidationErrors());
};

export const getIdToken = (redirect=false) => (dispatch) => {
  return firebase.auth().currentUser.getIdTokenResult(true)
    .then(res => {
      dispatch(loginSuccess(res));
      dispatch(finalizeLoginSuccess());
      if (redirect) {
        redirectToCurrentDayPage();
      }
      return res.token;
    })
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(loginFailure(getLoginValidationMessage(err.code)))
    });
};

const isTokenRefreshNeeded = (idToken, expTimestamp) => {
  if (idToken && expTimestamp) {
    const expiresIn = expTimestamp - Date.now();
    if (expiresIn <= 0) {
      return true;
    }
    return false;
  }
  return true;
};

export const getValidIdToken = () => (dispatch, getState) => {
  const { idToken, expTimestamp } = getState()?.authReducer;

  if (isTokenRefreshNeeded(idToken, expTimestamp)) {
    return dispatch(getIdToken());
  } else {
    return idToken;
  }
};

// Custom-claim vocabulary (contract with the backend claims backfill):
//   role                     : 'admin' | 'manager' (global)
//   bloodbanks/{bloodbankId} : 'admin' | 'manager' (explicit bank grant)
//   hf/{hfId}                : 'admin' | 'manager' — grants EVERY bloodbank
//                              under that Helseforetak
// Precedence: an explicit bloodbanks/* grant overrides an inherited hf/* one
// for that bank; the effective bank set is the union of both.
const BLOODBANK_CLAIM_PREFIX = 'bloodbanks/';
const HF_CLAIM_PREFIX = 'hf/';

const isStaffRole = (role) => role === 'admin' || role === 'manager';

// Derive the effective [{bloodbankId, role, name?}] bank set from ID-token
// claims. Claims are the ONLY authorization source — never the admins doc.
export const deriveBankAccessFromClaims = async (claims) => {
  const hfRoles = {};
  Object.keys(claims || {}).forEach((key) => {
    if (key.startsWith(HF_CLAIM_PREFIX)) {
      hfRoles[key.slice(HF_CLAIM_PREFIX.length)] = claims[key];
    }
  });

  const effective = new Map();

  // Inherited hf/* grants first, so explicit bloodbanks/* grants override
  // them below (precedence rule). There is no flat hf field on bloodbank
  // docs to query on, so expand via one read of the (small) collection.
  if (Object.keys(hfRoles).length > 0) {
    const snapshot = await db.collection('bloodbanks').get();
    snapshot.forEach((doc) => {
      const hfId = getHfIdFromReference(doc.data()?.reference);
      if (hfId && hfRoles[hfId]) {
        effective.set(doc.id, {
          bloodbankId: doc.id,
          role: hfRoles[hfId],
          name: doc.data()?.name || null,
        });
      }
    });
  }

  Object.keys(claims || {}).forEach((key) => {
    if (key.startsWith(BLOODBANK_CLAIM_PREFIX)) {
      const bloodbankId = key.slice(BLOODBANK_CLAIM_PREFIX.length);
      effective.set(bloodbankId, {
        bloodbankId,
        role: claims[key],
        name: effective.get(bloodbankId)?.name || null,
      });
    }
  });

  return Array.from(effective.values());
};

// Resolve display names for banks that arrived without one (explicit
// bloodbanks/* claim grants carry only the id).
const prepareBloodbanks = async (access) => {
  const preparedBloodbanks = [];

  for (let index = 0; index < access.length; index++) {
    const { bloodbankId, name = null, role } = access[index];
    if (name) {
      preparedBloodbanks.push({ bloodbankId, bloodbankName: name, role });
    } else {
      const bloodbankRef = firebase.firestore().collection('bloodbanks').doc(bloodbankId);
      const doc = await bloodbankRef.get();
      const bloodbankName = doc.data()?.name;
      preparedBloodbanks.push({ bloodbankId, bloodbankName, role });
    }
  }

  return preparedBloodbanks;
};

// {bloodbankId: hfId} for every bloodbank doc, so a bank granted through an
// HF-level `access` entry can take that entry's position in the picker.
const loadBankHfIds = async () => {
  const snapshot = await db.collection('bloodbanks').get();
  const bankHfIds = {};
  snapshot.forEach((doc) => {
    const hfId = getHfIdFromReference(doc.data()?.reference);
    if (hfId) bankHfIds[doc.id] = hfId;
  });
  return bankHfIds;
};

// CMT-40: present the claims-derived bank set in the order of the user's own
// `admins/{uid}.access` array — the order sysadmin shows — instead of the
// claim key order. The admins doc decides ORDER only; the bank set and the
// roles are exactly what the claims said (orderBanksByAccess never adds,
// drops or re-roles). A missing/unreadable doc keeps the claims order.
const applyAccessOrder = async (preparedBloodbanks, accessOrder) => {
  if (!Array.isArray(accessOrder) || accessOrder.length === 0) return preparedBloodbanks;

  let bankHfIds = {};
  const hasHfEntries = accessOrder.some((entry) => entry && entry.hfId && !entry.bloodbankId);
  if (hasHfEntries) {
    try {
      bankHfIds = await loadBankHfIds();
    } catch (orderError) {
      // Order is cosmetic: HF-granted banks then simply sort last, by name.
    }
  }
  return orderBanksByAccess(preparedBloodbanks, accessOrder, bankHfIds);
};

const fillBloodBanks = async (dispatch, {access, accessOrder, roleName, profileStorageRef, id}, phone) => {
  const preparedBloodbanks = await applyAccessOrder(await prepareBloodbanks(access), accessOrder);

  dispatch(setToAllBloodbanks(preparedBloodbanks));
  // Empty effective bank set with a valid role: setCurrentBloodbank(undefined)
  // is safe (reducer optional-chains) and the bank picker renders empty.
  dispatch(setCurrentBloodbank(preparedBloodbanks[0]));
  dispatch(getProfileData({ roleName, profileStorageRef, phone, uid: id }));
}

// Keep a single rendered RecaptchaVerifier for the whole session. Creating a
// second verifier and rendering it into the same container throws "reCAPTCHA
// has already been rendered in this element" (e.g. sign out then sign in again),
// because for invisible reCAPTCHA Firebase's clear() does not remove the widget
// from the DOM. Instead we reuse the instance and reset its widget, which clears
// the previously consumed/expired token so the next verify() solves a fresh
// challenge — avoiding both the re-render crash and stale-token reuse
// (auth/invalid-app-credential).
function getRecaptchaVerifier() {
  if (window.recaptchaVerifier) {
    try {
      if (
        window.recaptchaWidgetId !== undefined &&
        window.grecaptcha &&
        typeof window.grecaptcha.reset === "function"
      ) {
        window.grecaptcha.reset(window.recaptchaWidgetId);
      }
    } catch (e) {
      // Reset is best-effort; fall back to reusing the existing verifier.
    }
    return window.recaptchaVerifier;
  }

  window.recaptchaVerifier = new firebase.auth.RecaptchaVerifier(
    "recaptcha-container",
    {
      size: "invisible",
      callback: () => {
        const { currentEmail, authSessionId } = store.getState().authReducer;
        store.dispatch(sendAuthEvent(authFlowEvents.RECAPTCHA_COMPLETED, { email: currentEmail, sessionId: authSessionId }));
      },
      "expired-callback": () => {
        const { currentEmail, authSessionId } = store.getState().authReducer;
        store.dispatch(sendAuthEvent(authFlowEvents.RECAPTCHA_FAILED, {
          email: currentEmail,
          sessionId: authSessionId,
          errorCode: "recaptcha-expired",
          errorMessage: "reCAPTCHA challenge expired before completion",
        }));
      },
    }
  );

  window.recaptchaVerifier.render().then((widgetId) => {
    window.recaptchaWidgetId = widgetId;
  });

  return window.recaptchaVerifier;
}

export const login = ({ email, password }) => async (dispatch, getState) => {
  // Guard against spam-clicks / double submits: redux dispatch is synchronous,
  // so a second rapid click sees loginLoading === true and bails before the
  // request (and its single-use reCAPTCHA token) is started.
  if (getState().authReducer.loginLoading) return;
  dispatch(setLoginLoading(true));
  dispatch(loginRequest());
  // resendCount is persisted to localStorage, so a leftover value from an
  // earlier session would hide the "resend code" link (shown only when
  // resendCount === 0). Reset it when a new login starts.
  dispatch(resetResendCount());
  dispatch(setCurrentEmail(email));

  const sessionId = generateSessionId();
  dispatch(setAuthSessionId(sessionId));
  const logAuthEvent = (eventType, extra = {}) =>
    dispatch(sendAuthEvent(eventType, { email, sessionId, ...extra }));

  const captchaResponse = await getRecaptchaToken('signInWithPassword');

  // Returned so callers/tests can await the whole chain; LoginForm ignores it.
  return signIn(email, password, captchaResponse)
    .then(async (res) => {
      try {
        await firebase.auth().signInWithEmailAndPassword(email, password);
      } catch (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          logAuthEvent(authFlowEvents.LOGIN_SUCCESS);
          dispatch(setIsInTwoFactor(true))
          redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
          firebase.auth().useDeviceLanguage();

          const resolver = error.resolver;
          const hints = resolver.hints;

          const phoneHint = hints.find(hint => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID);

          if (phoneHint) {
            const phoneInfoOptions = {
              multiFactorHint: phoneHint,
              session: resolver.session
            };

            const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

            try {
              const verifier = getRecaptchaVerifier();
              const verificationId = await phoneAuthProvider.verifyPhoneNumber(
                phoneInfoOptions,
                verifier
              );

              logAuthEvent(authFlowEvents.MFA_START_SUCCESS);
              dispatch(setCurrentCode({ verificationId, resolver }));
              dispatch(setIsInTwoFactor(true))
              redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
              firebase.auth().useDeviceLanguage();

            } catch (smsError) {
              console.error("Error sending SMS:", smsError);
              logAuthEvent(authFlowEvents.MFA_START_FAILURE, { errorCode: smsError?.code, errorMessage: smsError?.message });
              dispatch(sendErrorLog(smsError.message, smsError.code));
              dispatch(loginFailure(smsError));
            }
          } else {
            logAuthEvent(authFlowEvents.MFA_START_FAILURE, { errorCode: 'no-phone-enrolled', errorMessage: 'MFA required, but no phone number is enrolled.' });
            dispatch(loginFailure({ message: "MFA required, but no phone number is enrolled." }));
          }
        } else {
          logAuthEvent(authFlowEvents.LOGIN_FAILURE, { errorCode: error?.code, errorMessage: error?.message });
          const blockedUrls = await checkExternalUrlAccess();
          if (blockedUrls.length > 0) {
            dispatch(setFirewallError(blockedUrls));
          } else if (isLoginUnavailable(error)) {
            // auth/network-request-failed etc.: Identity Toolkit never
            // answered — the credentials were not judged.
            dispatch(loginUnavailable(error));
          } else {
            dispatch(loginFailure(error));
          }
        }
      }
    })
    .catch(async (err) => {
      console.log(err)
      // /v1/auth/login did not answer 2xx. A network failure (no response —
      // DNS/TLS/refused, e.g. the browser resolving an old or wrong API host),
      // a 5xx, or a 404/410 from a host that is not api-internal is NOT a
      // credential verdict: tell the user the service is unavailable. Any
      // other 4xx (401 wrong password / captcha, 403, 429, …) is the backend's
      // deliberate answer and keeps the generic credential message.
      const unavailable = isLoginUnavailable(err);
      // errorCode must be a string: a bare HTTP status (401 from /v1/auth/login)
      // is a number and the backend rejects the whole event with 422.
      const failureCode = err?.code ?? (err?.status != null ? `http-${err.status}` : undefined);
      logAuthEvent(authFlowEvents.LOGIN_FAILURE, { errorCode: failureCode, errorMessage: err?.message });
      dispatch(sendSigninAttemptLog({
        email,
        status: 'failure',
        failureReason: unavailable ? loginFailureReason.SERVICE_UNAVAILABLE : loginFailureReason.WRONG_CREDENTIALS,
        type: 'login',
      }));
      dispatch(sendErrorLog(err.message, err.code || err.status));
      const blockedUrls = await checkExternalUrlAccess();
      if (blockedUrls.length > 0) {
        dispatch(setFirewallError(blockedUrls));
      } else if (unavailable) {
        dispatch(loginUnavailable(err));
      } else {
        dispatch(loginFailure());
      }
    })
    .finally(() => {
      dispatch(setLoginLoading(false));
    });
};

export const confirmCode = (code) => async (dispatch, getState) => {
  // Guard against spam-clicks: bail if a finalize request is already running.
  if (getState().authReducer.finalizeLoginLoading) return;
  dispatch(finalizeLoginRequest());
  const { verificationId, resolver } = getState().authReducer.currentCode;
  const { currentEmail, authSessionId } = getState().authReducer;

  if (!verificationId || !resolver) {
    console.error("MFA session info is missing. Redirecting to login.");
    window.location.href = "/login";
    return;
  }

  const credential = firebase.auth.PhoneAuthProvider.credential(
    verificationId,
    code
  );

  const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

  try {
    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);

    const phone = userCredential.user.phoneNumber;
    const uid = userCredential.user.uid;
    const email = userCredential.user.email;

    // Authorization comes from the ID-token custom claims ONLY (role + bank
    // grants) — force-refreshed so we see the claims minted for this login.
    const tokenResult = await userCredential.user.getIdTokenResult(true);
    const { claims } = tokenResult;
    const roleName = claims.role;

    if (!isStaffRole(roleName)) {
      // No usable role claim → unauthorized. Never fall back to the admins
      // doc's roleName — that would silently revive the doc/claims drift
      // this migration removes.
      dispatch(sendAuthEvent(authFlowEvents.MFA_FINALIZE_FAILURE, { email, sessionId: authSessionId, errorCode: 'missing-role-claim', errorMessage: 'ID token carries no admin/manager role claim' }));
      dispatch(sendSigninAttemptLog({ email, status: 'failure', failureReason: loginFailureReason.TWO_FACTOR_ERROR, type: 'login' }));
      dispatch(sendErrorLog('ID token carries no admin/manager role claim', 'missing-role-claim'));
      dispatch(twoFactorRejected());
      dispatch(finalizeLoginFailure());
      firebase.auth().signOut().catch(() => {});
      return;
    }

    // The admins doc is read for DISPLAY data only (profile image ref; phone
    // comes from the MFA credential above) plus the staff feature flags
    // (`features`, CMT-15) that gate nav items — the backend enforces the
    // flags on its own — and the `access` array, used ONLY to order the bank
    // picker like sysadmin does (CMT-40). Nothing authorization-relevant may
    // come from it.
    let profileStorageRef = null;
    let features = {};
    let accessOrder = null;
    try {
      const doc = await db.collection("admins").doc(uid).get();
      profileStorageRef = doc.data()?.profileStorageRef || null;
      features = normalizeFeatures(doc.data()?.features);
      accessOrder = doc.data()?.access || null;
    } catch (displayDataError) {
      // Display-only data — a failed read must not block login.
    }
    dispatch(setFeatures(features));

    dispatch(sendAuthEvent(authFlowEvents.MFA_FINALIZE_SUCCESS, { email, sessionId: authSessionId }));

    const access = await deriveBankAccessFromClaims(claims);
    // MUST complete before the redirect below: the dashboard's mount fetches
    // build /v1/bloodbanks/{id}/... from currentBloodbank — unawaited, the
    // redirect wins the race and the first fetches go out with an empty id
    // (observed as bloodbanks//reservations 404s right after sign-in).
    await fillBloodBanks(dispatch, {access, accessOrder, roleName, profileStorageRef, id: uid }, phone);

    dispatch(sendSigninAttemptLog({ email, status: 'success', type: 'login' }));
    // Reuse the token result fetched above instead of a second force-refresh
    // (this is exactly what getIdToken(true) would do).
    dispatch(loginSuccess(tokenResult));
    dispatch(finalizeLoginSuccess());
    redirectToCurrentDayPage();

    // const user = firebase.auth().currentUser;
    // console.log(firebase.auth.PhoneAuthProvider.PROVIDER_ID);

    // if(firebase.auth.PhoneAuthProvider.PROVIDER_ID) {
    //   await user.unlink(firebase.auth.PhoneAuthProvider.PROVIDER_ID);
    //   console.log("Phone unlinked successfully")
    // }


  } catch (err) {
    // Only actual code-entry failures are "invalid code" — anything else
    // (e.g. a blocking-function rejection AFTER a correct OTP) must not be
    // mislabeled, or debugging starts from the wrong end.
    const isCodeEntryError = [
      'auth/invalid-verification-code',
      'auth/missing-verification-code',
      'auth/code-expired',
    ].includes(err?.code);
    // Transport trouble (auth/network-request-failed, Firestore unavailable)
    // is neither a bad code nor a rejection — say the service is unavailable.
    const unavailable = !isCodeEntryError && isLoginUnavailable(err);
    const finalizeEvent = err?.code === 'auth/invalid-verification-code'
      ? authFlowEvents.OTP_INVALID
      : authFlowEvents.MFA_FINALIZE_FAILURE;
    const failureReason = isCodeEntryError
      ? loginFailureReason.TWO_FACTOR_WRONG_CODE
      : (unavailable ? loginFailureReason.SERVICE_UNAVAILABLE : loginFailureReason.TWO_FACTOR_ERROR);
    dispatch(sendAuthEvent(finalizeEvent, { email: currentEmail, sessionId: authSessionId, errorCode: err?.code, errorMessage: err?.message }));
    dispatch(sendSigninAttemptLog({email: currentEmail, status: 'failure', failureReason, type: 'login' }));
    dispatch(sendErrorLog(err.message, err.code));
    if (isCodeEntryError) {
      dispatch(twoFactorError());
    } else if (unavailable) {
      dispatch(twoFactorUnavailable());
    } else {
      dispatch(twoFactorRejected());
    }
    dispatch(finalizeLoginFailure());
  }
};

export const resendCode = () => (dispatch, getState) => {
  // The resend trigger is a <div onClick>, so guard against spam-clicks here:
  // overlapping verifyPhoneNumber calls would each consume a single-use
  // reCAPTCHA token, race, and fire duplicate SMS.
  if (getState().authReducer.resendLoading) return;

  // During MFA login we only have the resolver session and a masked phone hint
  // (not the full number), so resend must re-run the MFA challenge through the
  // resolver — not signInWithPhoneNumber, which expects a real phone number and
  // returns a result shape confirmCode can't finalize.
  const { currentCode } = getState().authReducer;
  const resolver = currentCode?.resolver;

  if (!resolver) {
    dispatch(twoFactorError());
    return;
  }

  dispatch(setResendLoading(true));
  firebase.auth().useDeviceLanguage();

  const phoneHint = resolver.hints.find(
    (hint) => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
  );

  const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

  phoneAuthProvider
    .verifyPhoneNumber(
      { multiFactorHint: phoneHint, session: resolver.session },
      getRecaptchaVerifier()
    )
    .then((verificationId) => {
      dispatch(setCurrentCode({ verificationId, resolver }));
      dispatch(addResend());
      redirect(`${routes.TWO_FACTOR_AUTH_CODE_SENT_ROUTE}`);
    })
    .catch((err) => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(twoFactorError());
    })
    .finally(() => {
      dispatch(setResendLoading(false));
    });
}


export const confirmCodeMobile = (code) => async (dispatch, getState) => {

  const { userId, currentPhone } = getState().authReducer;
  const user = firebase.auth().currentUser;
  try {
    const session = await user.multiFactor.getSession();

    const phoneInfoOptions = {
      phoneNumber: currentPhone,
      session,
    };

    const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneInfoOptions,
      getRecaptchaVerifier()
    );

    const credential = firebase.auth.PhoneAuthProvider.credential(
      verificationId,
      code
    );

    const assertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);

    await user.multiFactor.enroll(assertion, "Primary Phone");
 
    const multiFactorUser = user.multiFactor
    const enrolledFactors = multiFactorUser.enrolledFactors;

    const phoneFactorToUnenroll = enrolledFactors.find(
      (factor) => factor.factorId === firebase.auth.PhoneAuthProvider.PROVIDER_ID && factor.displayName !== 'My Authenticator App' && factor.phoneNumber !== currentPhone
    );

    if (!phoneFactorToUnenroll) {
      dispatch({ type: 'MFA_UNENROLL_NOT_NEEDED' });
      return;
    }

    await multiFactorUser.unenroll(phoneFactorToUnenroll);


    dispatch(resetResendCount());

    await addPhoneRequest(`/v1/staff/${userId}`, { phone: currentPhone });
    redirect(routes.CHANGE_PHONE_SUCCESS_ROUTE);
  } catch (err) {
    if(err.code === 'auth/requires-recent-login') {
      redirect(`${routes.VALIDATE_PASSWORD_ROUTE}`);
      return;
    }
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError())
  }
}

export const resendCodeMobile = () => (dispatch, getState) => {

  const { phone } = getState().authReducer;

  firebase.auth().useDeviceLanguage();



  firebase.auth().signInWithPhoneNumber(`+${phone}`, getRecaptchaVerifier())
  .then((res) => {
    dispatch(setCurrentCode(res));
    dispatch(addResend());
    redirect(`${routes.TWO_FACTOR_AUTH_CODE_SENT_ROUTE}`);
  })
  .catch((err) => {
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError())
  })
}

// Session restore (hard refresh): re-derive authorization from the ID-token
// claims so the localStorage snapshot never becomes an authorization source
// of its own. Uses the cached token (no force refresh) — the SDK refreshes
// it itself when expired.
export const rehydrateAuthorizationFromClaims = (user) => async (dispatch, getState) => {
  try {
    const res = await user.getIdTokenResult();
    const { claims } = res;
    const roleName = claims.role;

    if (!isStaffRole(roleName)) {
      // No usable role claim → unauthorized; never fall back to persisted
      // state or the admins doc's roleName.
      dispatch(sendErrorLog('ID token carries no admin/manager role claim on session restore', 'missing-role-claim'));
      dispatch(logoutRequest());
      return;
    }

    // Updates idToken/expTimestamp and roleName (reducer reads claims.role).
    dispatch(loginSuccess(res));

    // Re-read the user's own admins doc so a feature flag flipped in sysadmin
    // takes effect on the next reload, not only on the next sign-in, and so
    // the bank picker follows the doc's `access` order (CMT-40). Display-only:
    // a failed read keeps the persisted features and the claims order.
    let accessOrder = null;
    try {
      const doc = await db.collection("admins").doc(user.uid).get();
      dispatch(setFeatures(normalizeFeatures(doc.data()?.features)));
      accessOrder = doc.data()?.access || null;
    } catch (featuresError) {
      // keep persisted features
    }

    const access = await deriveBankAccessFromClaims(claims);
    const preparedBloodbanks = await applyAccessOrder(await prepareBloodbanks(access), accessOrder);
    dispatch(setToAllBloodbanks(preparedBloodbanks));

    // Keep the persisted bank selection when it is still in the effective
    // set; otherwise fall back to the first allowed bank (or empty).
    const { currentBloodbank } = getState().authReducer;
    const stillAllowed = preparedBloodbanks.find((bank) => bank.bloodbankId === currentBloodbank);
    if (!stillAllowed) {
      dispatch(setCurrentBloodbank(preparedBloodbanks[0]));
    }
  } catch (err) {
    // Transient failure (e.g. offline): keep the persisted state rather than
    // logging the user out — API RBAC still enforces authorization.
    dispatch(sendErrorLog(err.message, err.code));
  }
};

export const verifyAuth = () => (dispatch) => {
  let initialAuthStateHandled = false;
  firebase.auth().onAuthStateChanged(user => {
    const isInitialAuthState = !initialAuthStateHandled;
    initialAuthStateHandled = true;

    if (!user) {
      // No Firebase session, but localStorage may still hold an idToken from
      // an earlier one (persisted redux state outliving the SDK session).
      // Clear ONLY the token so PrivateRoute stops rendering authenticated
      // screens — a full auth-state reset here would also wipe the in-flight
      // MFA state/error this callback races with (it fires after the
      // deliberate signOut on a post-finalize rejection).
      dispatch(updateToken(null));
      dispatch(logoutRequest());
      return;
    }

    // Only on the initial callback (page load with a persisted Firebase
    // session). Later signed-in transitions are handled by confirmCode.
    if (isInitialAuthState) {
      dispatch(rehydrateAuthorizationFromClaims(user));
    }
  })
};

export const logoutRequest = () => async (dispatch) => {
  const user = firebase.auth().currentUser;
  if (user) {
    const email = user ? user?.email : '';
    dispatch(userLogoutRequest());
    firebase.auth().signOut()
      .then(() => {

        dispatch(sendSigninAttemptLog({ email, status: 'success', type: 'logout' }))
        // saveState({});
      })
      .catch(err => {
        dispatch(sendErrorLog(err.message, err.code));
        dispatch(sendSigninAttemptLog({ email, status: 'failure', type: 'logout', failureReason: err.message || '' }))
        dispatch(loginFailure());
      });
  }
};

export const forgotPassword = (email) => (dispatch) => {
  dispatch(forgotPasswordRequest());
  redirect(routes.FORGOT_PASSWORD_EMAIL_SENT_ROUTE);
  apiForgotPassword(email, window.location.origin);
};

export const resetPasswordRequest = ({verificationCode, newPassword}) => async (dispatch) => {
  await dispatch(forgotPasswordRequest());
  const passwordPolicy = await getPasswordPolicy();
  const error = passwordValidationError(newPassword, passwordPolicy);
  if (error) {
    dispatch(forgotPasswordFailure(error));
    return;
  }
  firebase.auth().confirmPasswordReset(verificationCode, newPassword)
    .then(() => redirect(routes.LOGIN_ROUTE))
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(forgotPasswordFailure(getForgotPasswordValidationMessage(err.code)));
    });
};

const updatePassword = (user, newPassword) => (dispatch) => {
  user.updatePassword(newPassword)
    .then(() => {
      dispatch(changePasswordSuccess());
      const dateFormat = "yyyy-MM-dd";
      const formattedDate = dateFns.format(new Date(), dateFormat);
      redirect(`${routes.DASHBOARD_DAY_ROUTE}/${formattedDate}`);
    })
    .catch(err => {
      dispatch(sendErrorLog(err.message, err.code));
      dispatch(changePasswordFailure(getNewPasswordValidationMessage(err.code)));
    });
};

export const getCodeMobilePassword = (oldPassword) => async (dispatch) => {
  firebase.auth().useDeviceLanguage();
  const user = firebase.auth().currentUser;
  const credential = firebase.auth.EmailAuthProvider.credential(user.email, oldPassword);

  try {
    await user.reauthenticateWithCredential(credential);
  } catch (error) {
    if(error.code === 'auth/multi-factor-auth-required') {
      dispatch(setIsInTwoFactor(true));
      firebase.auth().useDeviceLanguage();
      redirect(routes.TWO_FACTOR_AUTH_ROUTE);

      const resolver = error.resolver;
      const hints = resolver.hints;

      const phoneHint = hints.find(
        (hint) => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID
      );

      if (phoneHint) {
        try {
          const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
          const verifier = getRecaptchaVerifier();
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            {
              multiFactorHint: phoneHint,
              session: resolver.session,
            },
            verifier
          );

          dispatch(setCurrentCode({ verificationId, resolver }));
          dispatch(setIsInTwoFactor(true));
          redirect(routes.CHANGE_PASSWORD_ENTER_CODE);

        } catch (smsError) {
          console.error("Error sending SMS:", smsError);
        }
      } else {
        dispatch(changePasswordFailure({newPasswordError: "MFA required, but no phone enrolled."}))
      }
    }
  }
}

export const confirmCodeMobilePassword = (code) => async (dispatch, getState) => {
  const { currentCode, passwords: { oldPassword, newPassword } } = getState().authReducer;

  try {
    const { verificationId, resolver } = currentCode;
    const phoneCredential = firebase.auth.PhoneAuthProvider.credential(verificationId, code);
    const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(phoneCredential);
    const userCredential = await resolver.resolveSignIn(multiFactorAssertion);

    const validationError =
      duplicatePasswordsError(oldPassword, newPassword) ||
      passwordValidationError(newPassword);

    if (validationError) {
      dispatch(changePasswordFailure({ newPasswordError: validationError }));
      return;
    }

    dispatch(updatePassword(userCredential.user, newPassword))

  } catch (err) {
    console.error("MFA confirm error:", err);
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError());
  } finally {
    dispatch(setPasswords({ oldPassword: null, newPassword: null }));
  }
};

export const reAuthenticateUser = ({ password }) => async (dispatch) => {
  const user = firebase.auth().currentUser;


  if (!user) {
    console.error("No user is currently signed in.");
    dispatch({ type: "USER_NOT_SIGNED_IN" });
    return;
  }

  try {
    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      password
    );

    await user.reauthenticateWithCredential(credential);

    dispatch({ type: "USER_REAUTH_SUCCESS" });

    return { success: true };
  } catch (err) {
    if (err.code === "auth/multi-factor-auth-required") {
      const resolver = err.resolver;
      const phoneInfoHint = resolver.hints[0];
      const session = resolver.session;

      const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();

      const phoneInfoOptions = {
        multiFactorHint: phoneInfoHint,
        session: session,
      };
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        getRecaptchaVerifier()
      );

      dispatch(setCurrentCode({ verificationId, resolver }));
      dispatch(setIsInTwoFactor(true))
      redirect(`${routes.TWO_FACTOR_AUTH_ROUTE}`);
      firebase.auth().useDeviceLanguage();

      return { success: false, mfaRequired: true, verificationId, resolver };
    }
    dispatch(sendErrorLog(err.message, err.code));
    return { success: false, error: err };
  }
};

export const changePhone = (phone) => async (dispatch) => {
  firebase.auth().useDeviceLanguage();

  const user = firebase.auth().currentUser;

  if (!user) {
    console.error("User is not signed in.");
    return;
  }

  try {

    const provider = new firebase.auth.PhoneAuthProvider();
    const verificationId = await provider.verifyPhoneNumber(`+${phone}`, getRecaptchaVerifier());


    dispatch(setPhone(`+${phone}`));
    dispatch(setCurrentCode(verificationId));
    dispatch(setCurrentCode({ verificationId }));
    redirect(routes.CHANGE_PHONE_ENTER_CODE);
  } catch (err) {
    if (
      err?.message?.includes("auth/too-many-requests") ||
      err?.message?.includes("auth/too-many-attempts") ||
      err?.message?.includes("auth/error-code:-39")
    ) {
      return toast.error(
        "You have reached your OTP confirmation limit for today. Please try again tomorrow.",
        { position: "bottom-right", style: { padding: "15px 20px" } }
      );
    }
    if (err.code === 'auth/requires-recent-login') {
      redirect(`${routes.VALIDATE_PASSWORD_ROUTE}`);
    }
    dispatch(sendErrorLog(err.message, err.code));
    dispatch(twoFactorError());
  }
};

export const changePassword = ({oldPassword, newPassword}) => (dispatch) => {
  dispatch(changePasswordRequest());
  const user = firebase.auth().currentUser;
  if (!user) {
    dispatch(changePasswordFailure("Invalid user"));
    redirect(routes.LOGIN_ROUTE);
    return;
  }

    dispatch(setPasswords({oldPassword, newPassword}));
    const validationError = duplicatePasswordsError(oldPassword, newPassword) || passwordValidationError(newPassword);
    if (validationError) {
      dispatch(changePasswordFailure({newPasswordError: validationError}))
    } else {
      dispatch(getCodeMobilePassword(oldPassword));
    }
    return;
};
